import { Pencil } from "lucide-react";
import { formatAddress, formatPhone } from "../../lib/api";
import { useLang } from "../../i18n/LangContext";

const Section = ({ title, onEdit, children, testId }) => {
  const { t } = useLang();
  return (
    <section className="panel p-5" data-testid={testId}>
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-gray-400">{title}</h3>
        {onEdit && (
          <button type="button" onClick={onEdit} className="inline-flex items-center gap-1 text-sm font-semibold text-[#FACC15] hover:underline" data-testid={`${testId}-edit-btn`}>
            <Pencil className="h-3.5 w-3.5" aria-hidden="true" /> {t("wizard.edit")}
          </button>
        )}
      </div>
      <div className="mt-3 space-y-1 text-white">{children}</div>
    </section>
  );
};

const vehicleLine = (v) => [v.year, v.make, v.model].filter(Boolean).join(" ");

export const Summary = ({ data, onEdit, photos = [], compact = false }) => {
  const { t } = useLang();
  const dest = data.destination ? formatAddress(data.destination) : data.needs_destination_help ? t("wizard.destHelpChosen") : t("wizard.noDestination");
  return (
    <div className="grid gap-4">
      <Section title={t("wizard.service")} onEdit={onEdit && (() => onEdit(0))} testId="review-service">
        <p className="text-lg font-bold">{t(`services.${data.service_id}`)[0]}</p>
      </Section>
      <Section title={t("wizard.pickupLabel")} onEdit={onEdit && (() => onEdit(1))} testId="review-pickup">
        <p>{formatAddress(data.pickup)}</p>
        {data.pickup.notes && <p className="text-sm text-gray-400 whitespace-pre-line">{data.pickup.notes}</p>}
      </Section>
      <Section title={t("wizard.destLabel")} onEdit={onEdit && (() => onEdit(1))} testId="review-destination">
        <p>{dest}</p>
        {data.destination?.notes && <p className="text-sm text-gray-400 whitespace-pre-line">{data.destination.notes}</p>}
      </Section>
      {!compact && (
      <Section title={t("wizard.vehicleLabel")} onEdit={onEdit && (() => onEdit(2))} testId="review-vehicle">
        <p>{vehicleLine(data.vehicle)} {data.vehicle.color && <span className="text-gray-400">· {data.vehicle.color}</span>}</p>
        {data.vehicle.type && <p className="text-sm text-gray-400">{t(`vehicleTypes.${data.vehicle.type}`)}</p>}
        {data.vehicle.safe_location !== undefined && data.vehicle.safe_location !== null && (
          <p className={`text-sm font-semibold ${data.vehicle.safe_location ? "text-emerald-300" : "text-red-400"}`}>{data.vehicle.safe_location ? t("wizard.safe") : t("wizard.unsafe")}</p>
        )}
        {data.vehicle.special_requirements && <p className="text-sm text-gray-400 whitespace-pre-line">{data.vehicle.special_requirements}</p>}
        {data.vehicle.description && <p className="text-sm text-gray-400 whitespace-pre-line">{data.vehicle.description}</p>}
        {photos.length > 0 && <p className="text-sm text-gray-400">{t("wizard.photos")}: {photos.length}</p>}
      </Section>
      )}
      {data.customer && !compact && (
        <Section title={t("wizard.contactLabel")} onEdit={onEdit && (() => onEdit(2))} testId="review-contact">
          <p>{data.customer.name}</p>
          <p className="font-mono">{formatPhone(data.customer.phone.startsWith("+") ? data.customer.phone : `+1${data.customer.phone.replace(/\D/g, "").slice(-10)}`)}</p>
          <p className="text-sm text-gray-400">{data.customer.contact_method === "text" ? t("wizard.text") : t("wizard.call")}{data.customer.email && ` · ${data.customer.email}`}</p>
          {data.customer.contact_notes && <p className="text-sm text-gray-400 whitespace-pre-line">{data.customer.contact_notes}</p>}
        </Section>
      )}
    </div>
  );
};
